import {
  useEffectWithController,
  useLoadableState,
  usePromise
} from '@carvana/merch-react-hooks';
import { useContext, useEffect, useRef } from 'react';
import DeviceContext, {
  useHasAccurateDeviceType,
  useIsMobile
} from '~/context/common/DeviceContext';
import StaticContext from '~/context/common/StaticContext';
import { useUserLocation, useIsLoggedIn } from '~/context/common/UserContext';
import type { AppliedFilters, RecommendedVehicle } from '~/contracts/common';
import fetchPersonalizedRecommendations from '~/services/common/fetchPersonalizedRecommendations';
import { BrowserCookies } from '~/utilities';
import { generateErrorLogger } from '~/utilities/loggers';

export default function useGetRecommendedVehicles(
  vehiclesToReturn: number,
  filters: Partial<AppliedFilters>,
  targetVehicleId: number | undefined
) {
  const { data, error, isLoaded, setData, setError, setLoading } =
    useLoadableState<RecommendedVehicle[]>([]);
  const mounted = usePromise();
  const isMobile = useIsMobile();
  const { browserName } = useContext(DeviceContext);
  const { clientId, eventCorrelationId } = useContext(StaticContext);
  const { zip = '' } = useUserLocation();
  const isLoggedIn = useIsLoggedIn();
  const hasAccurateDeviceType = useHasAccurateDeviceType();
  const isMobileRef = useRef(isMobile);

  useEffect(() => {
    isMobileRef.current = isMobile;
  }, [isMobile]);

  useEffectWithController(
    controller => {
      if (!hasAccurateDeviceType) return;
      if (!targetVehicleId) {
        setData([]);

        return;
      }

      async function fetchVehicles() {
        if (!targetVehicleId) return [];

        setLoading();

        return fetchPersonalizedRecommendations(
          {
            analyticsData: {
              correlationId: eventCorrelationId,
              searchRequestId: '',
              isMobileDevice: isMobileRef.current,
              browser: browserName,
              filterSource: 'notSpecified',
              referrer: ''
            },
            browserCookieId: BrowserCookies.get('BrowserCookieId') ?? '',
            clientId,
            filters: {
              ...filters
            },
            numVehiclesToReturn: vehiclesToReturn,
            vehicleId: targetVehicleId,
            zip5: zip
          },
          isLoggedIn,
          controller
        );
      }

      mounted(fetchVehicles())
        .then(vehicles => {
          setData(vehicles);
        })
        .catch(
          generateErrorLogger(
            'Get recommended vehicles',
            { clientId },
            { callback: setError, controller }
          )
        );
    },
    [
      browserName,
      clientId,
      eventCorrelationId,
      filters,
      hasAccurateDeviceType,
      isLoggedIn,
      mounted,
      setData,
      setError,
      setLoading,
      targetVehicleId,
      vehiclesToReturn,
      zip
    ]
  );

  return {
    error,
    isLoaded,
    vehicles: data
  };
}
